import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

import { devicesApi, positionsApi, alertsApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Modal } from '../../components/common/Modal';
import { Button } from '../../components/common/Button';
import { LoadingSpinner, LoadingContent } from '../../components/common/LoadingSpinner';

// Fix Leaflet marker icons
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Device {
  id: string;
  imei: string;
  name: string | null;
  model: string | null;
  deviceType: 'LOAN' | 'SUBSCRIPTION';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  isOverdue: boolean;
  isInRepo: boolean;
  isAggressive: boolean;
  isOffline: boolean;
  lastOfflineAt: string | null;
  vehiclePlate: string | null;
  vehicleBrand: string | null;
  vehicleModel: string | null;
  vehicleYear: number | null;
  vehicleColor: string | null;
  companyId: string;
  clientId: string | null;
  createdAt: string;
  updatedAt: string;
  client?: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
  };
}

interface Position {
  deviceId: string;
  latitude: number;
  longitude: number;
  speed: number | null;
  heading: number | null;
  altitude: number | null;
  ignition: boolean | null;
  deviceTime: string;
  serverTime: string;
}

interface Alert {
  id: string;
  type: string;
  severity: string;
  title: string;
  description: string | null;
  status: string;
  createdAt: string;
}

// Component to center map on position
function MapCenter({ position }: { position: [number, number] }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position, 15);
    }
  }, [position, map]);

  return null;
}

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isEmployee = user?.role === 'EMPLOYEE';
  const canEdit = isAdmin || isEmployee;

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Device>>({});

  // Fetch device details
  const { data: deviceData, isLoading: isLoadingDevice } = useQuery({
    queryKey: ['device', id],
    queryFn: async () => {
      const response = await devicesApi.get(id!);
      return response.data.data as Device;
    },
    enabled: !!id,
  });

  // Fetch last position
  const { data: positionData, isLoading: isLoadingPosition } = useQuery({
    queryKey: ['devicePosition', id],
    queryFn: async () => {
      const response = await devicesApi.getLastPosition(id!);
      return response.data.data as Position | null;
    },
    enabled: !!id,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch recent positions for mini track
  const { data: recentTrackData } = useQuery({
    queryKey: ['deviceRecentTrack', id],
    queryFn: async () => {
      const from = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(); // Last 4 hours
      const to = new Date().toISOString();
      const response = await positionsApi.getHistory(id!, { from, to });
      return response.data.data as Position[];
    },
    enabled: !!id,
    refetchInterval: 60000, // Refetch every minute
  });

  // Fetch device alerts
  const { data: alertsData } = useQuery({
    queryKey: ['deviceAlerts', id],
    queryFn: async () => {
      const response = await alertsApi.list({ deviceId: id, limit: 10 });
      return response.data.data as Alert[];
    },
    enabled: !!id,
  });

  // Mutations
  const updateDeviceMutation = useMutation({
    mutationFn: (data: Partial<Device>) => devicesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device', id] });
      setShowEditModal(false);
    },
  });

  const markOverdueMutation = useMutation({
    mutationFn: () => devicesApi.markOverdue(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['device', id] }),
  });

  const markNotOverdueMutation = useMutation({
    mutationFn: () => devicesApi.markNotOverdue(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['device', id] }),
  });

  const markRepoMutation = useMutation({
    mutationFn: () => devicesApi.markRepo(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['device', id] }),
  });

  const markNotRepoMutation = useMutation({
    mutationFn: () => devicesApi.markNotRepo(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['device', id] }),
  });

  const device = deviceData;
  const position = positionData;
  const recentTrack = recentTrackData || [];
  const alerts = alertsData || [];

  useEffect(() => {
    if (device) {
      setEditForm({
        name: device.name || '',
        vehiclePlate: device.vehiclePlate || '',
        vehicleBrand: device.vehicleBrand || '',
        vehicleModel: device.vehicleModel || '',
        vehicleYear: device.vehicleYear || undefined,
        vehicleColor: device.vehicleColor || '',
      });
    }
  }, [device]);

  if (isLoadingDevice) {
    return <LoadingContent message="Cargando dispositivo..." />;
  }

  if (!device) {
    return (
      <div className="p-6">
        <div className="text-center py-10">
          <p className="text-gray-600">Dispositivo no encontrado</p>
          <Button onClick={() => navigate('/devices')} className="mt-4">
            Volver a dispositivos
          </Button>
        </div>
      </div>
    );
  }

  const mapCenter: [number, number] = position
    ? [position.latitude, position.longitude]
    : [18.4861, -69.9312]; // Default to Santo Domingo

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800';
      case 'SUSPENDED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAlertSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Link to="/devices" className="hover:text-blue-600">
              Dispositivos
            </Link>
            <span>/</span>
            <span>{device.name || device.imei}</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {device.name || device.imei}
          </h1>
          <p className="text-gray-600">IMEI: {device.imei}</p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button onClick={() => setShowEditModal(true)}>
              Editar
            </Button>
          )}
          <Button variant="secondary" onClick={() => navigate(`/history?device=${id}`)}>
            Ver historial
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Device info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Status card */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Estado</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Estado:</span>
                <span className={`px-2 py-1 text-xs rounded ${getStatusColor(device.status)}`}>
                  {device.status}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tipo:</span>
                <span className={`px-2 py-1 text-xs rounded ${
                  device.deviceType === 'LOAN'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {device.deviceType === 'LOAN' ? 'Préstamo' : 'Suscripción'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Conexión:</span>
                <span className={`px-2 py-1 text-xs rounded ${
                  device.isOffline
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {device.isOffline ? 'Offline' : 'Online'}
                </span>
              </div>

              {/* Flags */}
              <div className="border-t pt-3 mt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Flags</p>
                <div className="flex flex-wrap gap-2">
                  {device.isOverdue && (
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                      Atrasado
                    </span>
                  )}
                  {device.isInRepo && (
                    <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded">
                      En Incaute
                    </span>
                  )}
                  {device.isAggressive && (
                    <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                      Agresivo
                    </span>
                  )}
                  {!device.isOverdue && !device.isInRepo && !device.isAggressive && (
                    <span className="text-sm text-gray-500">Ninguno</span>
                  )}
                </div>
              </div>

              {/* Quick actions for flags */}
              {canEdit && (
                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Acciones rapidas</p>
                  <div className="flex flex-wrap gap-2">
                    {device.isOverdue ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => markNotOverdueMutation.mutate()}
                        disabled={markNotOverdueMutation.isPending}
                      >
                        Quitar Atrasado
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => markOverdueMutation.mutate()}
                        disabled={markOverdueMutation.isPending}
                      >
                        Marcar Atrasado
                      </Button>
                    )}
                    {device.isInRepo ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => markNotRepoMutation.mutate()}
                        disabled={markNotRepoMutation.isPending}
                      >
                        Quitar Incaute
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markRepoMutation.mutate()}
                        disabled={markRepoMutation.isPending}
                      >
                        Marcar Incaute
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Vehicle info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Vehículo</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Placa:</span>
                <span className="font-medium">{device.vehiclePlate || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Marca:</span>
                <span>{device.vehicleBrand || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Modelo:</span>
                <span>{device.vehicleModel || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Ano:</span>
                <span>{device.vehicleYear || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Color:</span>
                <span>{device.vehicleColor || '-'}</span>
              </div>
            </div>
          </div>

          {/* Client info */}
          {device.client && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Cliente</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Nombre:</span>
                  <span className="font-medium">{device.client.name}</span>
                </div>
                {device.client.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span>{device.client.email}</span>
                  </div>
                )}
                {device.client.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Teléfono:</span>
                    <span>{device.client.phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Información</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Modelo GPS:</span>
                <span>{device.model || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Creado:</span>
                <span>{format(new Date(device.createdAt), 'dd/MM/yyyy')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Actualizado:</span>
                <span>{formatDistanceToNow(new Date(device.updatedAt), { addSuffix: true, locale: es })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Map and alerts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Map */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Ubicacion actual</h2>
              {position && (
                <div className="text-sm text-gray-600">
                  Última actualizacion: {formatDistanceToNow(new Date(position.deviceTime), { addSuffix: true, locale: es })}
                </div>
              )}
            </div>
            <div className="h-96">
              {isLoadingPosition ? (
                <div className="h-full flex items-center justify-center">
                  <LoadingSpinner />
                </div>
              ) : (
                <MapContainer
                  center={mapCenter}
                  zoom={15}
                  className="h-full w-full"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {position && (
                    <>
                      <MapCenter position={mapCenter} />

                      {/* Recent track */}
                      {recentTrack.length > 1 && (
                        <Polyline
                          positions={recentTrack.map(p => [p.latitude, p.longitude])}
                          color="#3b82f6"
                          weight={3}
                          opacity={0.6}
                        />
                      )}

                      {/* Current position marker */}
                      <Marker position={mapCenter}>
                        <Popup>
                          <div className="min-w-48">
                            <div className="font-semibold text-sm mb-2">
                              {device.name || device.imei}
                            </div>
                            <div className="text-xs space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Velocidad:</span>
                                <span>{position.speed?.toFixed(0) || 0} km/h</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Dirección:</span>
                                <span>{position.heading?.toFixed(0) || 0}°</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Ignicion:</span>
                                <span>{position.ignition ? 'Encendido' : 'Apagado'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Hora:</span>
                                <span>{format(new Date(position.deviceTime), 'HH:mm:ss')}</span>
                              </div>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    </>
                  )}
                </MapContainer>
              )}
            </div>
            {position && (
              <div className="p-4 border-t bg-gray-50 grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 block">Latitud</span>
                  <span className="font-medium">{position.latitude.toFixed(6)}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Longitud</span>
                  <span className="font-medium">{position.longitude.toFixed(6)}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Velocidad</span>
                  <span className="font-medium">{position.speed?.toFixed(0) || 0} km/h</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Ignicion</span>
                  <span className={`font-medium ${position.ignition ? 'text-green-600' : 'text-gray-600'}`}>
                    {position.ignition ? 'Encendido' : 'Apagado'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Recent alerts */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Alertas recientes</h2>
              <Link to={`/alerts?deviceId=${id}`} className="text-sm text-blue-600 hover:underline">
                Ver todas
              </Link>
            </div>
            {alerts.length === 0 ? (
              <p className="text-gray-500 text-sm">No hay alertas recientes</p>
            ) : (
              <div className="space-y-3">
                {alerts.slice(0, 5).map((alert) => (
                  <div
                    key={alert.id}
                    className={`p-3 rounded border ${getAlertSeverityColor(alert.severity)}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">{alert.title}</p>
                        {alert.description && (
                          <p className="text-xs mt-1 opacity-80">{alert.description}</p>
                        )}
                      </div>
                      <div className="text-xs opacity-70">
                        {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true, locale: es })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar dispositivo"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateDeviceMutation.mutate(editForm);
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre
            </label>
            <input
              type="text"
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Placa del vehiculo
            </label>
            <input
              type="text"
              value={editForm.vehiclePlate || ''}
              onChange={(e) => setEditForm({ ...editForm, vehiclePlate: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marca
              </label>
              <input
                type="text"
                value={editForm.vehicleBrand || ''}
                onChange={(e) => setEditForm({ ...editForm, vehicleBrand: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Modelo
              </label>
              <input
                type="text"
                value={editForm.vehicleModel || ''}
                onChange={(e) => setEditForm({ ...editForm, vehicleModel: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ano
              </label>
              <input
                type="number"
                value={editForm.vehicleYear || ''}
                onChange={(e) => setEditForm({ ...editForm, vehicleYear: parseInt(e.target.value) || undefined })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <input
                type="text"
                value={editForm.vehicleColor || ''}
                onChange={(e) => setEditForm({ ...editForm, vehicleColor: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowEditModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={updateDeviceMutation.isPending}
            >
              {updateDeviceMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
